/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Backdrop, Box, Button, CircularProgress, Link, TextField, useTheme, useMediaQuery } from "@mui/material"
import { collection, doc, addDoc, DocumentReference, onSnapshot, deleteDoc } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db, functions } from "../../firebase"
import { httpsCallable } from "firebase/functions"
import { tokens } from "../../theme"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import dayjs from "dayjs"
import * as EmailValidator from 'email-validator'
import { HunterStatusIcon } from "./lib/HunterStatusIcon"

const sendMail = httpsCallable(functions, 'sendMail')
const mailTemplate = (id: string) => {
return `
狩猟者登録の準備を開始しました。
以下リンクより狩猟者情報の入力をお願いします。

https://traceability.rindou.info/hunters/${id}/regist
`
}

const Hunters = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [hunterEmail, setHunterEmail] = useState<string|null>('')
  const [items, setItems] = useState<Array<object>>([])
  const [emailError, setEmailError] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>("")
  const [prosessing, setProcessing] = useState<boolean>(false)
  const huntersRef = collection(db, 'hunters')

  const columns = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
      sortComparator: gridNumberComparator,
    },
    {
      field: "name",
      headerName: "狩猟者名",
      cellClassName: "name-column--cell",
      width: 120,
    },
    {
      field: "status",
      headerName: "状態",
      width: 80,
      renderCell: (params: any) => (
        <HunterStatusIcon hunterId={params.row.id} status={params.row.status} />
      ),
    },
    {
      field: "cancel",
      headerName: "削除",
      width: 70,
      renderCell: (params: any) => (
        params.row.status === "init" && < Link color="secondary" style={{ cursor: "pointer" }} onClick = {() => cancel(params.row.id)}> 削除</Link >
      ),
    }
  ] : [
    {
      field: "No",
      headerName: "Mo",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "receptionDatetime",
      headerName: "受付日時",
      flex: 1,
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 1,
    },
    {
      field: "name",
      headerName: "狩猟者名",
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "status",
      headerName: "状態",
      flex: 1,
      renderCell: (params: any) => (
        <HunterStatusIcon hunterId={params.row.id} status={params.row.status} />
      ),
    },
    {
      field: "cancel",
      headerName: "キャンセル",
      flex: 1,
      renderCell: (params: any) => (
        params.row.status === "init" && < Link color="secondary" style={{ cursor: "pointer" }} onClick = {() => cancel(params.row.id)}> キャンセル</Link >
      ),
    }
  ]

  useEffect(() => {
    const unsubscribe = onSnapshot(huntersRef, ({ docs }) => {
      setItems(docs.map((_, i) => {
        return ({ No: i+1, ..._.data(), id: _.id })
      }))
    })
    return unsubscribe
  }, [])

  const onChange = (e: any) => {
    setHunterEmail(e.target.value)
  }

  const add = async () => {
    if (!hunterEmail) return
    if (!EmailValidator.validate(hunterEmail)) {
      setEmailError(true)
      setErrorMsg("メールアドレスが不正です")
      return
    }
    setProcessing(true)
    setHunterEmail("")
    setEmailError(false)
    await addDoc(collection(db, "hunters"), { email: hunterEmail, receptionDatetime: dayjs().format("YYYY/MM/DD HH:mm"), status: "init" }).then(async (docRef: DocumentReference) => {
      await sendMail({
        to: hunterEmail,
        subject: "[りんどう] 狩猟者情報の入力をお願いします",
        type: "text",
        contents: mailTemplate(docRef.id)
      })
      toast.success("狩猟者情報の登録を開始しました")
    }).catch(e => {
      toast.error(JSON.stringify(e))
    }).finally(() => {
      setProcessing(false)
    })
  }

  const cancel = async (id: string) => {
    if(!window.confirm("狩猟者情報を削除しますか？")) return
    await deleteDoc(doc(collection(db, "hunters"), id))
      .then(() => {
        toast.success("狩猟者情報を削除しました")
      })
      .catch(e => {
        toast.error(JSON.stringify(e))
      })
  }

  return (
    <Box m={isMobile ? 2 : 5}>
      <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="start" alignContent="center" gap={isMobile ? 1 : 0}>
        <TextField error={emailError} value={hunterEmail} helperText={errorMsg} style={{ width: isMobile ? '100%' : 300 }} label="Eメールアドレスを入力" onChange={onChange}/>
        <Box display="flex" m={1} justifyContent="center">
          <Button variant="contained" color="info" onClick={add}>狩猟者情報追加</Button>
        </Box>
      </Box>
      <Box
        m={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "80vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid rows={items} columns={columns} />
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default Hunters
